import { useEffect, useRef, useState } from 'react';
import { ArrowLeft, ArrowRight, Pause, Play, Volume2, VolumeX } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useLang } from '../i18n';
import { BrandFilm } from './BrandFilm';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const mediaUrl = (value) => value?.startsWith('/api/') ? `${process.env.REACT_APP_BACKEND_URL}${value}` : value;

export const EcoMoments = () => {
  const { t } = useLang();
  const [items, setItems] = useState([]);
  const [active, setActive] = useState(null);
  const [muted, setMuted] = useState(true);
  const [page, setPage] = useState(0);
  const rail = useRef(null);
  const videos = useRef(new Map());

  useEffect(() => {
    const videoNodes = videos.current;
    fetch(`${API}/eco-moments`).then((response) => response.ok ? response.json() : []).then((data) => setItems(Array.isArray(data) ? data : [])).catch(() => setItems([]));
    return () => videoNodes.forEach((video) => video.pause());
  }, []);

  if (!items.length) return null;
  const brandFilm = items.find((item) => item.moment_type === 'brand_film');
  const moments = items.filter((item) => (item.moment_type || 'moment') === 'moment');
  const move = (direction) => rail.current?.scrollBy({ left: direction * Math.min(420, rail.current.clientWidth * .86), behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
  const onRailScroll = () => {
    const node = rail.current;
    if (!node || !node.children.length) return;
    const childWidth = node.children[0].getBoundingClientRect().width + 20; // card + gap
    setPage(Math.round(node.scrollLeft / childWidth));
  };
  const goToPage = (i) => {
    const node = rail.current;
    if (!node || !node.children.length) return;
    const childWidth = node.children[0].getBoundingClientRect().width + 20;
    node.scrollTo({ left: i * childWidth, behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
  };
  const togglePlay = async (item) => {
    const video = videos.current.get(item.id);
    if (!video) return;
    if (active === item.id && !video.paused) { video.pause(); setActive(null); return; }
    videos.current.forEach((other, id) => { if (id !== item.id) other.pause(); });
    await video.play(); setActive(item.id);
  };

  return <>
    {brandFilm && <BrandFilm film={brandFilm} />}
    {moments.length > 0 && <section className="overflow-hidden bg-eco-deep py-20 md:py-24" data-testid="eco-moments">
      <div className="mx-auto max-w-7xl px-5 md:px-8">
        <div className="flex items-end justify-between gap-4"><div><p className="eco-eyebrow text-eco-gold">{t('moments.kicker')}</p><h2 className="eco-section-title mt-3 text-white">{t('moments.title')}</h2></div><div className="hidden gap-2 md:flex"><button onClick={() => move(-1)} aria-label={t('moments.previous')} className="grid h-11 w-11 place-items-center rounded-full border border-white/25 text-white hover:border-eco-gold"><ArrowLeft size={18} /></button><button onClick={() => move(1)} aria-label={t('moments.next')} className="grid h-11 w-11 place-items-center rounded-full border border-white/25 text-white hover:border-eco-gold"><ArrowRight size={18} /></button></div></div>
        <div ref={rail} tabIndex="0" aria-label={t('moments.carousel')} onScroll={onRailScroll} className="mt-9 flex snap-x snap-mandatory gap-5 overflow-x-auto overscroll-x-contain pb-4 [scrollbar-width:thin] [scrollbar-color:#c9a961_transparent]">
          {moments.map((item) => {
            const relation = item.related_experience_slug ? `/experiences/${item.related_experience_slug}` : item.related_journey_slug ? `/journeys/${item.related_journey_slug}` : null;
            return <article key={item.id} className="w-[82vw] max-w-[360px] shrink-0 snap-start overflow-hidden rounded-2xl border border-white/10 bg-white/5" data-testid={`eco-moment-${item.id}`}>
              <div className="relative aspect-[4/5] bg-black"><video ref={(node) => { if (node) videos.current.set(item.id, node); else videos.current.delete(item.id); }} src={mediaUrl(item.video_url)} poster={mediaUrl(item.poster_url) || undefined} preload="metadata" muted={muted} playsInline onEnded={() => setActive(null)} className="h-full w-full object-cover" aria-label={item.poster_alt || item.title} /><div className="absolute inset-x-0 bottom-0 flex justify-between bg-gradient-to-t from-black/75 to-transparent p-4 pt-16"><button onClick={() => togglePlay(item)} aria-label={active === item.id ? t('moments.pause') : t('moments.play')} className="grid h-11 w-11 place-items-center rounded-full bg-white text-eco-deep">{active === item.id ? <Pause size={18} /> : <Play size={18} />}</button><button onClick={() => setMuted((value) => !value)} aria-label={muted ? t('moments.unmute') : t('moments.mute')} className="grid h-11 w-11 place-items-center rounded-full bg-black/45 text-white">{muted ? <VolumeX size={18} /> : <Volume2 size={18} />}</button></div></div>
              <div className="p-5"><p className="text-xs font-semibold uppercase tracking-[.14em] text-eco-gold">{[item.destination, item.island].filter(Boolean).join(' · ')}</p><h3 className="mt-2 text-xl font-semibold text-white">{item.title}</h3>{item.caption && <p className="mt-2 text-sm leading-relaxed text-white/65">{item.caption}</p>}{relation && <Link to={relation} className="eco-btn-text mt-4 text-eco-gold">{item.cta_label || (item.related_experience_slug ? t('moments.viewExperience') : t('moments.exploreJourney'))} <ArrowRight size={14} /></Link>}</div>
            </article>;
          })}
        </div>
        {moments.length > 1 && (
          <div className="mt-5 flex justify-center gap-2" data-testid="eco-moments-pagination">
            {moments.map((item, i) => (
              <button
                key={item.id}
                type="button"
                data-testid={`eco-moments-dot-${i}`}
                aria-label={`${t('moments.title')} ${i + 1}`}
                onClick={() => goToPage(i)}
                className={`h-1.5 rounded-full transition-all duration-500 ${i === page ? 'w-7 bg-eco-gold' : 'w-1.5 bg-white/30 hover:bg-white/60'}`}
              />
            ))}
          </div>
        )}
      </div>
    </section>}
  </>;
};
